<template>
  <ContentModal v-model="show" :title="modalTitle" size="lg">
    <template #title>
      <div>
        <h5 class="modal-title mb-0">{{ modalTitle }}</h5>
        <span v-if="locationName" class="text-xs text-secondary">{{ locationName }}</span>
      </div>
    </template>

    <!-- Loading spinner -->
    <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-sm text-secondary mb-0">Loading volunteer summary...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="d-flex flex-column align-items-center py-5">
      <i class="material-icons text-danger mb-2" style="font-size:48px;">error_outline</i>
      <p class="text-sm text-danger mb-2">{{ error }}</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetchSummary">Retry</button>
    </div>

    <!-- Chart -->
    <VolunteerSummaryChart
      v-else
      :summary="resolvedData"
      :personName="personName"
      :actionUris="actionUris"
      @navigate="handleNavigate"
    />
  </ContentModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ContentModal from '@/components/Modal/ContentModal.vue';
import VolunteerSummaryChart from './VolunteerSummaryChart.vue';
import api from '@/config/api';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  personName: { type: String, default: '' },
  locationName: { type: String, default: '' },
  /** Pre-loaded volunteer_summary object (no fetch needed) */
  prefetchedData: { type: Object, default: null },
  /** When provided without prefetchedData, triggers a lazy fetch */
  personId: { type: [String, Number], default: null },
  locationId: { type: [String, Number], default: null },
  /** Quick-link action URIs: { userPreferences, userAvailability, signUpAssignments } */
  actionUris: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue', 'navigate']);

const loading = ref(false);
const error = ref('');
const fetchedData = ref(null);

const show = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const modalTitle = computed(() =>
  props.personName ? `${props.personName}'s Volunteer Summary` : 'My Volunteer Summary'
);

const resolvedData = computed(() => props.prefetchedData || fetchedData.value || {});

// Lazy fetch when the modal opens and no prefetched data is available
watch(show, (open) => {
  if (open && !props.prefetchedData && props.personId && props.locationId) {
    fetchSummary();
  }
  // Reset fetched data when modal closes
  if (!open) {
    fetchedData.value = null;
    error.value = '';
  }
});

async function fetchSummary() {
  loading.value = true;
  error.value = '';
  try {
    const response = await api.get('/auth/person-volunteer-summary', {
      params: { personid: props.personId, locationid: props.locationId },
    });
    fetchedData.value = response.data?.volunteer_summary || response.data || {};
  } catch (err) {
    console.error('Error fetching volunteer summary:', err);
    error.value = err.response?.data?.message || 'Failed to load volunteer summary. Please try again.';
  } finally {
    loading.value = false;
  }
}

function handleNavigate(moduleId) {
  emit('navigate', moduleId);
}
</script>
